import type { PathParams } from "./types";

export function joinUrl(...urls: Array<string | undefined>) {
  return urls.filter(Boolean).join("/");
}

export function joinRoute<T>(...fns: Array<((params: T) => string) | string>) {
  // return (params: T) => joinUrl(fn(params), url);
  return (params: T) =>
    joinUrl(...fns.map((fn) => (typeof fn === "function" ? fn(params) : fn)));
}

// ------------------------------------------------------
// isUrlActive
// ------------------------------------------------------
export type ActiveType = "exact" | "inclusive";
export function isUrlActive(args: {
  href: string;
  activeType?: ActiveType;
  pathname: string;
}) {
  const { href, activeType = "inclusive", pathname } = args;
  return href
    ? activeType === "exact"
      ? pathname === href
      : pathname.startsWith(href) &&
        [undefined, "/", "?", "#"].includes(pathname[href.length])
    : false;
}

/**
 * ```ts
 * const params = parsePathParams<{
 *   orgUid: string;
 *   installedAppUid: string;
 *   fileUid: string;
 *   commentUid: string;
 * }>(
 *   "orgs/{orgUid}/apps/{installedAppUid}/files/{fileUid}/comments/{commentUid}",
 *   "orgs/123/apps/456/files/789/comments/101"
 * );
 *
 * // params = {
 * //   orgUid: "123",
 * //   installedAppUid: "456",
 * //   fileUid: "789",
 * //   commentUid: "101",
 * // }
 * ```
 */
export function parsePathParams<T extends object>(
  template: string,
  path: string
): T | undefined {
  const regexPattern = template.replace(
    /\{([^}]+)\}/g,
    (_, param) => `(?<${param}>[^/]+)`
  );

  const regex = new RegExp(`^${regexPattern}`);
  const match = regex.exec(path);

  if (!match) {
    return undefined;
  }

  return match.groups as T | undefined;
}

export function createParsePathParams<T extends object>(template: string) {
  return (path: string) => parsePathParams<T>(template, path);
}

export function getPathByTemplte(
  template: string,
  params: Record<string, any>
) {
  return template.replace(/\{([^}]+)\}/g, (_, param) => params[param] ?? "");
}

export function createGetPath<T extends string>(template: T) {
  const fn = (params: PathParams<T>) => getPathByTemplte(template, params);
  fn.template = template;

  return fn as {
    (params: PathParams<T>): string;
    template: T;
    /** only for type */
    type: PathParams<T>;
  };
}
export type GetPathWithTemplate<T extends string> = ReturnType<
  typeof createGetPath<T>
>;
